import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faEye,
  faEyeSlash,
  faPen,
  faPlus,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Badge, Button, Card, Form, Modal, Spinner } from "@themesberg/react-bootstrap";
import {
  DeleteApiRequest,
  GetApiRequest,
  PostApiRequest,
  PutApiRequest,
  placeholderImageDataUrl,
  resolveImageUrl,
} from "../services/api";
import { toast } from "../components/Toaster";

const TITLE_MAX = 60;
const SECTION_EXPERIENCE_MAX = 50;
const SEARCH_DEBOUNCE_MS = 300;

const AUTOMATIC_DESCRIPTIONS = {
  for_you: "Fills automatically from each user's preferred categories.",
  trending_near_you: "Fills automatically from experiences close to the user.",
  hidden_gems: "Fills automatically from the Hidden Gems category.",
  weekend_picks: "Fills automatically from events happening this weekend.",
};

const errorMessage = (e, fallback) => e?.response?.data?.message || fallback;

/** Copy of `list` with the item at `index` moved one place by `delta` (-1 or 1). */
function moved(list, index, delta) {
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(index + delta, 0, item);
  return next;
}

function experienceThumbnail(experience) {
  const urls = experience.fileAssets?.[0]?.urls;
  const raw = urls?.low || urls?.medium || urls?.high || urls?.original || experience.images?.[0];
  return resolveImageUrl(raw, "120") || placeholderImageDataUrl(48, 48);
}

function ExperienceRow({ experience, children }) {
  return (
    <div className="d-flex align-items-center py-2 border-bottom">
      <img
        src={experienceThumbnail(experience)}
        alt=""
        width={48}
        height={48}
        className="rounded me-3"
        style={{ objectFit: "cover", flexShrink: 0 }}
      />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="fw-bold text-truncate">{experience.title}</div>
        <small className="text-muted">
          {experience.category || "No category"}
          {experience.state !== "active" && (
            <Badge bg="warning" text="dark" className="ms-2">
              Not live: {experience.state}
            </Badge>
          )}
        </small>
      </div>
      <div className="ms-2 text-nowrap">{children}</div>
    </div>
  );
}

function TitleModal({ heading, initialTitle, saveLabel, onSave, onClose }) {
  const [title, setTitle] = useState(initialTitle);
  const [saving, setSaving] = useState(false);
  const trimmed = title.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!trimmed || saving) return;
    setSaving(true);
    const saved = await onSave(trimmed);
    if (!saved) setSaving(false);
  };

  return (
    <Modal show centered onHide={onClose}>
      <Form onSubmit={submit}>
        <Modal.Header>
          <Modal.Title className="h5">{heading}</Modal.Title>
          <Button variant="close" aria-label="Close" onClick={onClose} />
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="home-section-title">
            <Form.Label>Section title</Form.Label>
            <Form.Control
              autoFocus
              value={title}
              maxLength={TITLE_MAX}
              placeholder="Best Hikes"
              onChange={(e) => setTitle(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!trimmed || saving}>
            {saveLabel}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function ExperiencesModal({ section, onSaved, onClose }) {
  const [selected, setSelected] = useState(section.experiences);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const latestSearch = useRef(0);

  useEffect(() => {
    const requestId = ++latestSearch.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await GetApiRequest("admin/experiences", {
          state: "active",
          search: search.trim(),
          limit: 20,
        });
        if (requestId === latestSearch.current) setResults(res.data.data.experiences || []);
      } catch (e) {
        if (requestId === latestSearch.current) toast.error("Failed to search experiences");
      } finally {
        if (requestId === latestSearch.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const selectedIds = new Set(selected.map((e) => e._id));
  const atLimit = selected.length >= SECTION_EXPERIENCE_MAX;

  const save = async () => {
    setSaving(true);
    try {
      await PutApiRequest(`admin/home-sections/${section.id}/experiences`, {
        experienceIds: selected.map((e) => e._id),
      });
      toast.success("Section saved");
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save section"));
      setSaving(false);
    }
  };

  return (
    <Modal show centered size="xl" onHide={onClose}>
      <Modal.Header>
        <Modal.Title className="h5">Experiences in “{section.title}”</Modal.Title>
        <Button variant="close" aria-label="Close" onClick={onClose} />
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-12 col-lg-6 mb-4 mb-lg-0">
            <h3 className="h6">Add experiences</h3>
            <Form.Control
              aria-label="Search experiences"
              placeholder="Search by name or description"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-2"
            />
            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              {searching && results.length === 0 && (
                <div className="text-center my-3">
                  <Spinner animation="border" size="sm" role="status" />
                </div>
              )}
              {!searching && results.length === 0 && (
                <p className="text-muted my-3">No experiences found.</p>
              )}
              {results.map((experience) => (
                <ExperienceRow key={experience._id} experience={experience}>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={selectedIds.has(experience._id) || atLimit}
                    onClick={() => setSelected([...selected, experience])}
                  >
                    {selectedIds.has(experience._id) ? "Added" : "Add"}
                  </Button>
                </ExperienceRow>
              ))}
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <h3 className="h6">
              In this section ({selected.length} of {SECTION_EXPERIENCE_MAX})
            </h3>
            <div style={{ maxHeight: 462, overflowY: "auto" }}>
              {selected.length === 0 && (
                <p className="text-muted my-3">
                  Nothing here yet. An empty section doesn't show in the app.
                </p>
              )}
              {selected.map((experience, index) => (
                <ExperienceRow key={experience._id} experience={experience}>
                  <Button
                    size="sm"
                    variant="light"
                    aria-label={`Move ${experience.title} up`}
                    disabled={index === 0}
                    onClick={() => setSelected(moved(selected, index, -1))}
                  >
                    <FontAwesomeIcon icon={faArrowUp} />
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="light"
                    aria-label={`Move ${experience.title} down`}
                    disabled={index === selected.length - 1}
                    onClick={() => setSelected(moved(selected, index, 1))}
                  >
                    <FontAwesomeIcon icon={faArrowDown} />
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="light"
                    aria-label={`Remove ${experience.title}`}
                    onClick={() => setSelected(selected.filter((e) => e._id !== experience._id))}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </ExperienceRow>
              ))}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={saving} onClick={save}>
          Save section
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function SectionSubtitle({ section }) {
  if (section.kind !== "curated") return AUTOMATIC_DESCRIPTIONS[section.kind] || "Fills automatically.";
  const count = section.experiences.length;
  if (count === 0) return "No experiences yet, so this doesn't show in the app.";
  return `${count} hand-picked experience${count === 1 ? "" : "s"}`;
}

export default function HomeSections() {
  const [sections, setSections] = useState(null);
  const [saving, setSaving] = useState(false);
  // { type: "create" | "rename" | "experiences" | "delete", section? }
  const [dialog, setDialog] = useState(null);
  const closeDialog = () => setDialog(null);

  const load = useCallback(async () => {
    try {
      const res = await GetApiRequest("admin/home-sections");
      setSections(res.data.data.sections);
    } catch (e) {
      setSections((current) => current || []);
      toast.error(errorMessage(e, "Failed to load home sections"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const move = async (index, delta) => {
    const previous = sections;
    const next = moved(sections, index, delta);
    setSections(next);
    setSaving(true);
    try {
      await PutApiRequest("admin/home-sections/order", { sectionIds: next.map((s) => s.id) });
    } catch (e) {
      setSections(previous);
      toast.error(errorMessage(e, "Failed to reorder sections"));
      // A section may have been added elsewhere; pick up the current list.
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (section) => {
    setSaving(true);
    try {
      await PutApiRequest(`admin/home-sections/${section.id}`, { isVisible: !section.isVisible });
      setSections((list) =>
        list.map((s) => (s.id === section.id ? { ...s, isVisible: !s.isVisible } : s))
      );
    } catch (e) {
      toast.error(errorMessage(e, "Failed to update section"));
    } finally {
      setSaving(false);
    }
  };

  const createSection = async (title) => {
    try {
      const res = await PostApiRequest("admin/home-sections", { title });
      await load();
      // The next thing anyone does with a new section is fill it.
      setDialog({ type: "experiences", section: res.data.data.section });
      return true;
    } catch (e) {
      toast.error(errorMessage(e, "Failed to create section"));
      return false;
    }
  };

  const renameSection = async (section, title) => {
    try {
      await PutApiRequest(`admin/home-sections/${section.id}`, { title });
      setSections((list) => list.map((s) => (s.id === section.id ? { ...s, title } : s)));
      closeDialog();
      return true;
    } catch (e) {
      toast.error(errorMessage(e, "Failed to rename section"));
      return false;
    }
  };

  const deleteSection = async (section) => {
    setSaving(true);
    try {
      await DeleteApiRequest(`admin/home-sections/${section.id}`);
      setSections((list) => list.filter((s) => s.id !== section.id));
      toast.success("Section deleted");
      closeDialog();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to delete section"));
    } finally {
      setSaving(false);
    }
  };

  if (sections === null) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-start flex-wrap my-4">
        <div className="me-3">
          <h1 className="h4 mb-1">Home Page</h1>
          <p className="text-muted mb-2">
            Sections appear in the app in this order. Changes go live as soon as they're saved.
          </p>
        </div>
        <Button variant="primary" onClick={() => setDialog({ type: "create" })}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          New section
        </Button>
      </div>

      {sections.map((section, index) => (
        <Card
          key={section.id}
          className="mb-2 shadow-sm"
          style={section.isVisible ? undefined : { opacity: 0.6 }}
        >
          <Card.Body className="d-flex align-items-center flex-wrap py-3">
            <div className="me-3 text-nowrap">
              <Button
                size="sm"
                variant="light"
                aria-label={`Move ${section.title} up`}
                disabled={index === 0 || saving}
                onClick={() => move(index, -1)}
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </Button>{" "}
              <Button
                size="sm"
                variant="light"
                aria-label={`Move ${section.title} down`}
                disabled={index === sections.length - 1 || saving}
                onClick={() => move(index, 1)}
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </Button>
            </div>
            <div className="flex-grow-1 my-1" style={{ minWidth: 200 }}>
              <div className="fw-bold">
                {section.title}
                {section.kind !== "curated" && (
                  <Badge bg="secondary" className="ms-2">
                    Automatic
                  </Badge>
                )}
                {!section.isVisible && (
                  <Badge bg="dark" className="ms-2">
                    Hidden
                  </Badge>
                )}
              </div>
              <small className="text-muted">
                <SectionSubtitle section={section} />
              </small>
            </div>
            <div className="my-1 text-nowrap">
              {section.kind === "curated" && (
                <Button
                  size="sm"
                  variant="primary"
                  className="me-1"
                  onClick={() => setDialog({ type: "experiences", section })}
                >
                  Edit experiences
                </Button>
              )}
              <Button
                size="sm"
                variant="light"
                className="me-1"
                aria-label={`Rename ${section.title}`}
                title="Rename"
                onClick={() => setDialog({ type: "rename", section })}
              >
                <FontAwesomeIcon icon={faPen} />
              </Button>
              <Button
                size="sm"
                variant="light"
                className="me-1"
                aria-label={`${section.isVisible ? "Hide" : "Show"} ${section.title}`}
                title={section.isVisible ? "Hide from the app" : "Show in the app"}
                disabled={saving}
                onClick={() => toggleVisible(section)}
              >
                <FontAwesomeIcon icon={section.isVisible ? faEye : faEyeSlash} />
              </Button>
              {section.kind === "curated" && (
                <Button
                  size="sm"
                  variant="light"
                  aria-label={`Delete ${section.title}`}
                  title="Delete"
                  onClick={() => setDialog({ type: "delete", section })}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>
      ))}

      {dialog?.type === "create" && (
        <TitleModal
          heading="New section"
          initialTitle=""
          saveLabel="Create section"
          onSave={createSection}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === "rename" && (
        <TitleModal
          heading="Rename section"
          initialTitle={dialog.section.title}
          saveLabel="Save"
          onSave={(title) => renameSection(dialog.section, title)}
          onClose={closeDialog}
        />
      )}
      {dialog?.type === "experiences" && (
        <ExperiencesModal
          section={dialog.section}
          onClose={closeDialog}
          onSaved={() => {
            closeDialog();
            load();
          }}
        />
      )}
      {dialog?.type === "delete" && (
        <Modal show centered onHide={closeDialog}>
          <Modal.Header>
            <Modal.Title className="h5">Delete “{dialog.section.title}”?</Modal.Title>
            <Button variant="close" aria-label="Close" onClick={closeDialog} />
          </Modal.Header>
          <Modal.Body>
            This removes the section from the home page. Its experiences stay in GoLocal.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={closeDialog}>
              Cancel
            </Button>
            <Button variant="danger" disabled={saving} onClick={() => deleteSection(dialog.section)}>
              Delete section
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}
